import {BOE_REPORTS} from '../data/boeReportNavigation';
// src/features/boe/components/BoardOfEducation.jsx

import React, { useState, useEffect } from "react";
import { useOutletContext } from "react-router-dom";
import {
  ArrowUturnLeftIcon,
  PencilSquareIcon,
} from "@heroicons/react/24/outline";

import { ReportPageHeader, ReportChapterHeader } from '../../../shared/ui/layout/ReportPage';
import BoeBudgetOverview from './BoeBudgetOverview';
import schoolBanner from '../images/high-school-banner.png';
import campusBanner from '../images/campus-banner.png';
import elementaryBanner from '../images/elementary-banner.png';
import labBanner from '../images/learning-lab-banner.png';
import guardians from '../images/guardians.png';
import BudgetCard from "../../townBudget/components/budgetCard";
import WarningMessage from "../../../shared/ui/feedback/WarningMessage";
import { HeaderToggleButton } from "../../../shared/ui/overlay/HeaderToggleButton";
import { useAuth } from "../../../app/context/AuthContext";

// These are USED in JSX, so they must be imported (re-exporting does not define them locally)
import { ProgramBudgetByYear } from "./ProgramBudgetByYear.jsx";
import { StaffSnapshotByYear } from "./StaffSnapshotByYear.jsx";
import { StudentEnrollment } from "./StudentEnrollment.jsx";
import { PPE } from "./PPE.jsx";

// If you truly need GPSBudgetHistory elsewhere, import it in the file that uses it.
// (Not needed for this component unless you render it here.)
// import { GPSBudgetHistory } from "./GPSBudgetHistory.jsx";

/**
 * All imported BoE program components. Each one renders the budget tables
 * for a specific program code and supports edit mode + local data overrides.
 */
import {
  Program0200,
  Program0300,
  Program0400,
  Program0500,
  Program0600,
  Program0700,
  Program0900,
  Program1000,
  Program1100,
  Program1300,
  Program1400,
  Program1500,
  Program1600,
  Program1900,
  Program2000,
  Program2100,
  Program2200,
  Program2300,
  Program2400,
  Program2700,
  Program2800,
  Program3000,
  Program3100,
  Program3200,
  Program3300,
  Program3400,
  Program3600,
} from "./programs";

/**
 * Collapsible wrapper used throughout the Board of Education view to group
 * program sections and summaries.
 *
 * @component
 * @param {Object} props
 * @param {string} props.title - Title displayed on the collapsible header.
 * @param {React.ReactNode} props.children - Body content shown when expanded.
 * @param {boolean} [props.defaultCollapsed=true] - Whether the section is collapsed by default.
 * @returns {JSX.Element}
 */
function CollapsibleSection({ title, children, defaultCollapsed = true }) {
  const [collapsed, setCollapsed] = useState(defaultCollapsed);
  const controlsId = `boe-${title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;

  return (
    <div className="bg-base-100 shadow-md rounded-lg p-2 border border-gray-300">
      <HeaderToggleButton reportNavigation title={title} controlsId={controlsId}
        ariaLabel={`Toggle ${title}`} isOpen={!collapsed} onToggle={() => setCollapsed(prev => !prev)} />

      {!collapsed && <div id={controlsId} className="bg-white p-4">{children}</div>}
    </div>
  );
}

/**
 * **BoardOfEducation Component**
 *
 * Full-screen Board of Education (BoE) budget interface.
 *
 * @component
 * @returns {JSX.Element}
 */
export function BoardOfEducation() {
  const [isEditing, setIsEditing] = useState(false);
  const [saveMessage, setSaveMessage] = useState("");

  const { currentUser } = useAuth();

  const userCanEdit =
    currentUser?.roles?.includes("admin") ||
    currentUser?.roles?.includes("boe");

  const { selectedYear = "" } = useOutletContext();

  const [numericYears, setNumericYears] = useState([]);
  const [boeData, setBoeData] = useState({});

  useEffect(() => {
    if (typeof selectedYear === "string") {
      const match = selectedYear.match(/FY(\d{4})\s*-\s*(\d{4})/);
      const yearNum = match ? Number(match[2]) : null;
      setNumericYears(yearNum ? [yearNum] : []);
    } else {
      setNumericYears([]);
    }
  }, [selectedYear]);

  const handleCancelAllChanges = () => {
    setBoeData({});
    setIsEditing(false);
    setSaveMessage("Changes canceled.");
    setTimeout(() => setSaveMessage(""), 2500);
  };

  const handleSaveAllChanges = async () => {
    if (numericYears.length === 0) return;

    const fiscalYear = numericYears[0];
    const yearKey = `${fiscalYear - 1}_${fiscalYear}`;

    // eslint-disable-next-line no-unused-vars
    void yearKey;

    setSaveMessage("Save functionality for BoE is not implemented yet.");
    setIsEditing(false);
    setTimeout(() => setSaveMessage(""), 2500);
  };

  if (!selectedYear || numericYears.length === 0) {
    return (
      <WarningMessage message="Please select a fiscal year to view Board of Education budget data." />
    );
  }

  return (
    <div className="min-h-screen bg-base-200 text-base-content flex flex-col">
      <div className="flex flex-1 w-full justify-center">
        <div className="w-full max-w-screen-2xl">
          <BudgetCard>
            <div className="space-y-8 sm:space-y-10">
              <div className="space-y-3 sm:space-y-4">
                <img src={schoolBanner} alt="Watercolor illustration of Glastonbury High School" className="h-auto w-full object-contain" />
                <ReportPageHeader editorial title="Board of Education Budget" headingAs="h2"
                  actions={<img src={guardians} alt="Glastonbury Guardians" className="h-16 w-16 object-contain sm:h-20 sm:w-20" />} />
              </div>
              <p className="text-base leading-7 text-slate-600">Explore how Glastonbury invests in its schools, supports students and staff, and plans for the years ahead. Open any report below to see the details.</p>
              <BoeBudgetOverview />
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="min-w-0 flex-1"><ReportChapterHeader title="Summaries" tone="lavender" image={campusBanner} imageAlt="Watercolor illustration of a Glastonbury school campus with solar panels" description="Start with the district budget, staffing, enrollment, and spending per pupil." /></div>

                {userCanEdit && (
                  <div className="flex items-center gap-3">
                    {!isEditing ? (
                      <button
                        onClick={() => setIsEditing(true)}
                        className="flex items-center gap-1 text-xs text-white bg-blue-600 hover:bg-blue-700 font-medium px-3 py-1 rounded-md shadow-sm"
                      >
                        <PencilSquareIcon className="w-4 h-4 text-white" />
                        Edit
                      </button>
                    ) : (
                      <>
                        <button
                          onClick={handleSaveAllChanges}
                          className="flex items-center gap-1 text-xs text-white bg-green-600 hover:bg-green-700 font-medium px-3 py-1 rounded-md shadow-sm"
                        >
                          💾 Save
                        </button>

                        <button
                          onClick={handleCancelAllChanges}
                          className="flex items-center gap-1 text-xs text-white bg-red-600 hover:bg-red-700 font-medium px-3 py-1 rounded-md shadow-sm"
                        >
                          <ArrowUturnLeftIcon className="w-4 h-4 text-white" />
                          Cancel
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>

              {saveMessage && (
                <div className="text-sm text-green-700 font-semibold bg-green-100 border border-green-300 rounded-md py-2 px-4 mb-2">
                  {saveMessage}
                </div>
              )}

              <CollapsibleSection title={BOE_REPORTS[0].title}>
                <ProgramBudgetByYear
                  isEditing={isEditing}
                  setBoeData={setBoeData}
                  defaultCollapsed={true}
                />
              </CollapsibleSection>

              <CollapsibleSection title={BOE_REPORTS[1].title} defaultCollapsed={true}>
                <StaffSnapshotByYear
                  isEditing={isEditing}
                  setBoeData={setBoeData}
                />
              </CollapsibleSection>

              <CollapsibleSection title={BOE_REPORTS[2].title} defaultCollapsed={true}>
                <StudentEnrollment />
              </CollapsibleSection>

              <CollapsibleSection title={BOE_REPORTS[3].title} defaultCollapsed={true}>
                <PPE />
              </CollapsibleSection>

              <ReportChapterHeader title="Programs" tone="orange" image={labBanner} imageAlt="Watercolor illustration of a school STEM and robotics learning lab" description="Explore the learning programs and student services behind the district budget." />

              <div className="space-y-4">
                <CollapsibleSection title={BOE_REPORTS[4].title} defaultCollapsed={true}>
                  <Program0200 isEditing={isEditing} setBoeData={setBoeData} />
                </CollapsibleSection>

                <CollapsibleSection
                  title={BOE_REPORTS[5].title}
                  defaultCollapsed={true}
                >
                  <Program0300 isEditing={isEditing} setBoeData={setBoeData} />
                </CollapsibleSection>

                <CollapsibleSection
                  title={BOE_REPORTS[6].title}
                  defaultCollapsed={true}
                >
                  <Program0400 isEditing={isEditing} setBoeData={setBoeData} />
                </CollapsibleSection>

                <CollapsibleSection
                  title={BOE_REPORTS[7].title}
                  defaultCollapsed={true}
                >
                  <Program0500 isEditing={isEditing} setBoeData={setBoeData} />
                </CollapsibleSection>

                <CollapsibleSection
                  title={BOE_REPORTS[8].title}
                  defaultCollapsed={true}
                >
                  <Program0600 isEditing={isEditing} setBoeData={setBoeData} />
                </CollapsibleSection>

                <CollapsibleSection
                  title={BOE_REPORTS[9].title}
                  defaultCollapsed={true}
                >
                  <Program0700 isEditing={isEditing} setBoeData={setBoeData} />
                </CollapsibleSection>

                <CollapsibleSection
                  title={BOE_REPORTS[10].title}
                  defaultCollapsed={true}
                >
                  <Program0900 isEditing={isEditing} setBoeData={setBoeData} />
                </CollapsibleSection>

                <CollapsibleSection
                  title={BOE_REPORTS[11].title}
                  defaultCollapsed={true}
                >
                  <Program1000 isEditing={isEditing} setBoeData={setBoeData} />
                </CollapsibleSection>

                <CollapsibleSection
                  title={BOE_REPORTS[12].title}
                  defaultCollapsed={true}
                >
                  <Program1100 isEditing={isEditing} setBoeData={setBoeData} />
                </CollapsibleSection>

                <CollapsibleSection
                  title={BOE_REPORTS[13].title}
                  defaultCollapsed={true}
                >
                  <Program1300 isEditing={isEditing} setBoeData={setBoeData} />
                </CollapsibleSection>

                <CollapsibleSection
                  title={BOE_REPORTS[14].title}
                  defaultCollapsed={true}
                >
                  <Program1400 isEditing={isEditing} setBoeData={setBoeData} />
                </CollapsibleSection>

                <CollapsibleSection
                  title={BOE_REPORTS[15].title}
                  defaultCollapsed={true}
                >
                  <Program1500 isEditing={isEditing} setBoeData={setBoeData} />
                </CollapsibleSection>

                <CollapsibleSection
                  title={BOE_REPORTS[16].title}
                  defaultCollapsed={true}
                >
                  <Program1600 isEditing={isEditing} setBoeData={setBoeData} />
                </CollapsibleSection>

                <CollapsibleSection
                  title={BOE_REPORTS[17].title}
                  defaultCollapsed={true}
                >
                  <Program1900 isEditing={isEditing} setBoeData={setBoeData} />
                </CollapsibleSection>

                <CollapsibleSection
                  title={BOE_REPORTS[18].title}
                  defaultCollapsed={true}
                >
                  <Program2000 isEditing={isEditing} setBoeData={setBoeData} />
                </CollapsibleSection>

                <CollapsibleSection
                  title={BOE_REPORTS[19].title}
                  defaultCollapsed={true}
                >
                  <Program2100 isEditing={isEditing} setBoeData={setBoeData} />
                </CollapsibleSection>

                <CollapsibleSection
                  title={BOE_REPORTS[20].title}
                  defaultCollapsed={true}
                >
                  <Program2200 isEditing={isEditing} setBoeData={setBoeData} />
                </CollapsibleSection>

                <CollapsibleSection
                  title={BOE_REPORTS[21].title}
                  defaultCollapsed={true}
                >
                  <Program2300 isEditing={isEditing} setBoeData={setBoeData} />
                </CollapsibleSection>

                <CollapsibleSection
                  title={BOE_REPORTS[22].title}
                  defaultCollapsed={true}
                >
                  <Program2400 isEditing={isEditing} setBoeData={setBoeData} />
                </CollapsibleSection>

                <CollapsibleSection
                  title={BOE_REPORTS[23].title}
                  defaultCollapsed={true}
                >
                  <Program2700 isEditing={isEditing} setBoeData={setBoeData} />
                </CollapsibleSection>

                <CollapsibleSection
                  title={BOE_REPORTS[24].title}
                  defaultCollapsed={true}
                >
                  <Program2800 isEditing={isEditing} setBoeData={setBoeData} />
                </CollapsibleSection>

                <CollapsibleSection
                  title={BOE_REPORTS[25].title}
                  defaultCollapsed={true}
                >
                  <Program3000 isEditing={isEditing} setBoeData={setBoeData} />
                </CollapsibleSection>

                <ReportChapterHeader title="School operations" tone="green" image={elementaryBanner} imageAlt="Watercolor illustration of a welcoming Glastonbury elementary school" description="The leadership, facilities, and services that keep our schools running." />

                <CollapsibleSection
                  title={BOE_REPORTS[26].title}
                  defaultCollapsed={true}
                >
                  <Program3100 isEditing={isEditing} setBoeData={setBoeData} />
                </CollapsibleSection>

                <CollapsibleSection
                  title={BOE_REPORTS[27].title}
                  defaultCollapsed={true}
                >
                  <Program3200 isEditing={isEditing} setBoeData={setBoeData} />
                </CollapsibleSection>

                <CollapsibleSection
                  title={BOE_REPORTS[28].title}
                  defaultCollapsed={true}
                >
                  <Program3300 isEditing={isEditing} setBoeData={setBoeData} />
                </CollapsibleSection>

                <CollapsibleSection
                  title={BOE_REPORTS[29].title}
                  defaultCollapsed={true}
                >
                  <Program3400 isEditing={isEditing} setBoeData={setBoeData} />
                </CollapsibleSection>

                <CollapsibleSection
                  title={BOE_REPORTS[30].title}
                  defaultCollapsed={true}
                >
                  <Program3600 isEditing={isEditing} setBoeData={setBoeData} />
                </CollapsibleSection>
              </div>
            </div>
          </BudgetCard>
        </div>
      </div>
    </div>
  );
}

export default BoardOfEducation;
